import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CornerDownLeft, Loader2, MessageSquare, Phone } from "lucide-react";
import {
  useDataProvider,
  useGetIdentity,
  useGetList,
  useNotify,
} from "ra-core";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { ClearableSearchInput } from "@/components/ui/clearable-search-input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FaviconAvatarImage } from "@/components/ui/FaviconAvatarImage";
import { Input } from "@/components/ui/input";
import { Pressable, RowButton } from "@/components/ui/pressable";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { workspaceEmailBrand } from "@/lib/workspaceBranding";
import { getContactAvatarSources } from "@/components/atomic-crm/providers/commons/getContactAvatar";
import { hasMemberCapability } from "@/components/atomic-crm/providers/commons/memberModuleAccess";
import type { AccessIdentity } from "@/components/atomic-crm/providers/commons/canAccess";
import type { CrmDataProvider } from "@/components/atomic-crm/providers/types";
import type { Contact } from "@/modules/types";
import { getInitials } from "@/modules/messages/conversationDisplay";
import {
  getContactDisplayName,
  getContactPhoneEntries,
} from "@/modules/messages/messageContactUtils";
import { useMessagesQuickAccessOptional } from "@/modules/messages/messagesQuickAccessContext";
import { useMessagingEnabled } from "@/modules/messages/useMessagingEnabled";
import { useTwilioVoice } from "@/modules/voice/useTwilioVoice";
import {
  formatUsPhoneDisplayFromAny,
  normalizeUsPhoneToE164,
} from "@/utils/phone";

const KEYS: Array<[string, string]> = [
  ["1", ""],
  ["2", "ABC"],
  ["3", "DEF"],
  ["4", "GHI"],
  ["5", "JKL"],
  ["6", "MNO"],
  ["7", "PQRS"],
  ["8", "TUV"],
  ["9", "WXYZ"],
  ["*", ""],
  ["0", "+"],
  ["#", ""],
];

/**
 * Start a text or a call: pick a contact (left) or dial a number (right).
 * Opened from the top bar's phone icon and from Messages.
 */
export const NewConversationDialog = ({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const notify = useNotify();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const { identity } = useGetIdentity();
  const { smsEnabled, voiceEnabled } = useMessagingEnabled();
  const { placeCall, isBusy } = useTwilioVoice(voiceEnabled);
  const quickAccess = useMessagesQuickAccessOptional();
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [phone, setPhone] = useState("");
  const [selected, setSelected] = useState<Contact | null>(null);
  const [pending, setPending] = useState<"call" | "chat" | null>(null);

  const canCall =
    voiceEnabled &&
    hasMemberCapability(
      identity as AccessIdentity | undefined,
      "voice.calls.make",
    );

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query.trim()), 200);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    if (open) return;
    setQuery("");
    setPhone("");
    setSelected(null);
  }, [open]);

  const { data: contacts = [], isPending: contactsPending } =
    useGetList<Contact>(
      "contacts",
      {
        filter: debouncedQuery ? { q: debouncedQuery } : {},
        pagination: { page: 1, perPage: 60 },
        sort: { field: "first_name", order: "ASC" },
      },
      { enabled: open, staleTime: 30_000 },
    );
  const withPhones = useMemo(
    () =>
      contacts.filter((contact) => getContactPhoneEntries(contact).length > 0),
    [contacts],
  );

  const { data: settings } = useQuery({
    queryKey: ["messaging-settings"],
    queryFn: () => dataProvider.getMessagingSettings(),
    enabled: open,
    staleTime: 30_000,
  });
  const lineNumber =
    settings?.messaging_provider === "telnyx"
      ? settings?.telnyx_phone_number
      : settings?.twilio_phone_number;
  const lineName = workspaceEmailBrand().name;

  const e164 = normalizeUsPhoneToE164(phone.trim());

  const pickContact = (contact: Contact) => {
    setSelected(contact);
    setPhone(getContactPhoneEntries(contact)[0]?.display ?? "");
  };

  const typeNumber = (value: string) => {
    setPhone(value);
    // A typed number that is not the picked contact's starts fresh.
    if (
      selected &&
      !getContactPhoneEntries(selected).some(
        (entry) => entry.e164 === normalizeUsPhoneToE164(value.trim()),
      )
    ) {
      setSelected(null);
    }
  };

  const startChat = async () => {
    if (!smsEnabled || !quickAccess) return;
    if (!selected && !e164) {
      notify("Pick a contact or enter a 10-digit number.", { type: "info" });
      return;
    }
    setPending("chat");
    try {
      if (selected) await quickAccess.openSms(selected);
      else if (e164) await quickAccess.openSmsToPhone(e164);
      onOpenChange(false);
    } finally {
      setPending(null);
    }
  };

  const startCall = async () => {
    if (!canCall) return;
    if (!e164) {
      notify("Enter a valid US phone number (10 digits or +1…).", {
        type: "warning",
      });
      return;
    }
    if (isBusy) {
      notify("Finish the current call before dialing another number.", {
        type: "info",
      });
      return;
    }
    setPending("call");
    try {
      await placeCall({ to: e164, contactId: selected?.id ?? null });
      onOpenChange(false);
    } catch (error) {
      notify(
        error instanceof Error ? error.message : "Could not start the call",
        { type: "error" },
      );
    } finally {
      setPending(null);
    }
  };

  const ready = Boolean(selected || e164);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="gap-0 p-0 sm:max-w-3xl">
        <DialogHeader className="border-b px-5 py-4">
          <DialogTitle>New conversation</DialogTitle>
          <DialogDescription className="sr-only">
            Pick a contact or dial a number, then text or call.
          </DialogDescription>
        </DialogHeader>

        <div className="grid md:grid-cols-2 md:divide-x">
          <section className="flex min-w-0 flex-col gap-3 p-5">
            <div>
              <p className="text-sm font-semibold">Contacts</p>
              <p className="text-xs text-muted-foreground">
                Pick someone from your address book
              </p>
            </div>
            <ClearableSearchInput
              value={query}
              onChange={setQuery}
              placeholder="Name, phone number, or email…"
              className="h-9"
            />
            <div className="h-72 overflow-y-auto pr-1">
              {contactsPending ? (
                <div className="space-y-2">
                  {Array.from({ length: 6 }).map((_, index) => (
                    <Skeleton key={index} className="h-11 w-full" />
                  ))}
                </div>
              ) : withPhones.length === 0 ? (
                <p className="px-2 py-6 text-center text-xs text-muted-foreground">
                  {debouncedQuery
                    ? "No contacts with a phone match this search."
                    : "No contacts with a phone yet."}
                </p>
              ) : (
                <ul className="space-y-0.5">
                  {withPhones.map((contact) => {
                    const name = getContactDisplayName(contact);
                    const active =
                      selected != null &&
                      String(selected.id) === String(contact.id);
                    return (
                      <li key={String(contact.id)}>
                        <RowButton
                          density="compact"
                          selected={active}
                          onClick={() => pickContact(contact)}
                          onDoubleClick={() => {
                            pickContact(contact);
                            void startChat();
                          }}
                          className="gap-2.5"
                        >
                          <Avatar className="size-8">
                            <FaviconAvatarImage
                              sources={getContactAvatarSources(
                                contact as never,
                              )}
                              alt={name}
                            />
                            <AvatarFallback className="text-2xs font-medium">
                              {getInitials(name)}
                            </AvatarFallback>
                          </Avatar>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-medium">
                              {name}
                            </span>
                            <span className="block truncate text-xs text-muted-foreground">
                              {getContactPhoneEntries(contact)[0]?.display}
                              {contact.company_name?.trim()
                                ? ` · ${contact.company_name.trim()}`
                                : ""}
                            </span>
                          </span>
                        </RowButton>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </section>

          <section className="flex min-w-0 flex-col gap-3 border-t p-5 md:border-t-0">
            <div>
              <p className="text-sm font-semibold">Phone number</p>
              <p className="text-xs text-muted-foreground">
                Enter a number to call or text directly
              </p>
            </div>
            <Input
              type="tel"
              inputMode="tel"
              value={phone}
              onChange={(event) => typeNumber(event.target.value)}
              placeholder="Enter number"
              className="h-9 rounded-full text-center tabular-nums"
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  void startChat();
                }
              }}
            />
            <p className="h-4 text-center text-xs text-muted-foreground">
              {selected
                ? getContactDisplayName(selected)
                : e164
                  ? formatUsPhoneDisplayFromAny(e164)
                  : ""}
            </p>
            <div className="mx-auto grid w-full max-w-64 grid-cols-3 gap-2.5">
              {KEYS.map(([digit, letters]) => (
                <Pressable
                  key={digit}
                  aria-label={`Dial ${digit}`}
                  onClick={() => typeNumber(`${phone}${digit}`)}
                  className="mx-auto flex size-14 flex-col items-center justify-center rounded-full bg-muted/60 transition-colors hover:bg-muted active:scale-95"
                >
                  <span className="text-xl leading-none font-medium tabular-nums">
                    {digit}
                  </span>
                  <span className="mt-0.5 h-3 text-3xs tracking-widest text-muted-foreground">
                    {letters}
                  </span>
                </Pressable>
              ))}
            </div>
          </section>
        </div>

        <div className="flex flex-col items-center gap-3 border-t px-5 py-4">
          {lineNumber ? (
            <p className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs">
              <span className="size-2 rounded-full bg-success" aria-hidden />
              <span className="font-medium">{lineName}</span>
              <span className="text-muted-foreground tabular-nums">
                {formatUsPhoneDisplayFromAny(lineNumber)}
              </span>
            </p>
          ) : null}
          <div className="grid w-full max-w-sm grid-cols-2 gap-2">
            <Button
              type="button"
              variant="secondary"
              disabled={!canCall || !e164 || pending != null || isBusy}
              onClick={() => void startCall()}
              title={canCall ? undefined : "Calling is not turned on"}
            >
              {pending === "call" ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Phone className="size-4" />
              )}
              {isBusy ? "On a call" : "Call"}
            </Button>
            <Button
              type="button"
              disabled={!smsEnabled || !ready || pending != null}
              onClick={() => void startChat()}
              title={smsEnabled ? undefined : "Texting is not turned on"}
            >
              {pending === "chat" ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <MessageSquare className="size-4" />
              )}
              Chat
              <CornerDownLeft className="ml-1 size-3.5 opacity-60" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/** Icon button that opens New conversation (top bar, Messages). */
export const NewConversationButton = ({
  className,
  label = "New conversation",
}: {
  className?: string;
  label?: string;
}) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        type="button"
        variant="secondary"
        size="icon"
        className={cn("shrink-0", className)}
        aria-label={label}
        title={label}
        onClick={() => setOpen(true)}
      >
        <Phone className="size-4" />
      </Button>
      <NewConversationDialog open={open} onOpenChange={setOpen} />
    </>
  );
};
